import React from 'react';

import type { EventAlertDialogProps } from '@looko-design-system';
import { EventAlertDialog } from '@looko-design-system';
import { atom, useAtom, useSetAtom } from 'jotai';

import type { PartialSome } from '@looko-corp/acloset-react-kit/src/utils';

type EventAlertDialogState = Omit<EventAlertDialogProps, 'open'>;

const eventAlertDialogDefaultValue: EventAlertDialogState = {
  content: undefined,
  confirmButtonText: '확인',
  confirmButtonColor: undefined,
  cancelButtonText: '취소',
  onClose: () => {},
  onConfirm: () => true,
};

type AtomType = EventAlertDialogState[];

const globalDialogEventAlertValues = atom<AtomType>([]);

/**
 * 글로벌 Alert 대화상자.
 *
 * NOTE: Dialog 로 되어있지만 notification 디자인 시스템과 일치함.
 *
 * @see https://www.figma.com/file/PFrAvDaThtf8qMSRc9pNaV/Looko---Design-System?type=design&node-id=769-51373&mode=design&t=n8dWZCvcx0B2EPnw-0
 */
export function GlobalEventAlertDialog() {
  const [state, update] = useAtom(globalDialogEventAlertValues);

  const currentDialog = state[0];

  const handleClose = React.useCallback(() => {
    update(value => {
      const [_removed, ...rest] = value;
      return rest;
    });

    currentDialog?.onClose?.();
  }, [update, currentDialog]);

  if (state.length === 0) {
    return;
  }

  return <EventAlertDialog {...currentDialog} open={currentDialog != null} onClose={handleClose} />;
}

/**
 * 글로벌 Alert 대화상자를 부르는 핸들러를 가져옵니다.
 *
 * @returns openDialog 대화 상자를 엽니다.
 * @returns deleteDialog 삭제 버튼이 있는 대화 상자를 엽니다.
 */
export function useGlobalEventAlertDialog() {
  const updateValue = useSetAtom(globalDialogEventAlertValues);

  return React.useMemo(() => {
    return {
      openDialog: (values: PartialSome<EventAlertDialogState, 'onConfirm'>) => {
        updateValue(prev => {
          return [...prev, { ...eventAlertDialogDefaultValue, ...values }];
        });
      },
      deleteDialog: (values: PartialSome<EventAlertDialogState, 'onConfirm'>) => {
        updateValue(prev => {
          return [
            ...prev,
            {
              ...eventAlertDialogDefaultValue,
              confirmButtonText: '삭제',
              confirmButtonColor: 'error',
              ...values,
            },
          ];
        });
      },
      resetDialog: () => {
        updateValue([]);
      },
    };
  }, [updateValue]);
}
